import { sql } from "drizzle-orm";
import { pgTable, text, varchar, timestamp, integer, boolean, jsonb, json, index, uniqueIndex } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email").notNull().unique(),
  password: text("password"),
  subscriptionTier: text("subscription_tier").notNull().default("free"),
  stripeCustomerId: text("stripe_customer_id"),
  stripeSubscriptionId: text("stripe_subscription_id"),
  subscriptionStatus: text("subscription_status"),
  subscriptionExpiresAt: timestamp("subscription_expires_at"),
  role: varchar("role", { length: 20 }).notNull().default("user"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const paymentRequests = pgTable("payment_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email").notNull(),
  userId: varchar("user_id").references(() => users.id),
  paymentMethod: text("payment_method").notNull(),
  receiptUrl: text("receipt_url").notNull(),
  receiptFileName: text("receipt_file_name"),
  receiptStoragePath: text("receipt_storage_path"),
  status: text("status").notNull().default("pending"),
  adminNotes: text("admin_notes"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  processedAt: timestamp("processed_at"),
});

export const userEvents = pgTable("user_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id),
  eventType: text("event_type").notNull(),
  channel: text("channel"),
  status: text("status"),
  subject: text("subject"),
  metadata: jsonb("metadata"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const leagues = pgTable("leagues", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  apiFootballId: integer("api_football_id").notNull().unique(),
  leagueCode: varchar("league_code", { length: 20 }).notNull().unique(),
  name: text("name").notNull(),
  country: varchar("country", { length: 100 }),
  countryCode: varchar("country_code", { length: 20 }),
  leagueType: varchar("league_type", { length: 50 }),
  logoUrl: text("logo_url"),
  isFreeTier: boolean("is_free_tier").notNull().default(false),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const teams = pgTable("teams", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  apiFootballId: integer("api_football_id").notNull().unique(),
  name: text("name").notNull(),
  shortName: varchar("short_name", { length: 50 }),
  tla: varchar("tla", { length: 10 }),
  logoUrl: text("logo_url"),
  country: varchar("country", { length: 100 }),
  countryCode: varchar("country_code", { length: 20 }),
  foundedYear: integer("founded_year"),
  venueName: text("venue_name"),
  venueCity: text("venue_city"),
  venueCapacity: integer("venue_capacity"),
  venueSurface: text("venue_surface"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const fixtures = pgTable("fixtures", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  apiFootballId: integer("api_football_id").notNull().unique(),
  leagueId: varchar("league_id").notNull().references(() => leagues.id),
  seasonYear: integer("season_year").notNull(),
  homeTeamId: varchar("home_team_id").notNull().references(() => teams.id),
  awayTeamId: varchar("away_team_id").notNull().references(() => teams.id),
  matchday: integer("matchday"),
  matchDate: timestamp("match_date").notNull(),
  fixtureDate: timestamp("fixture_date"),
  status: varchar("status", { length: 20 }).notNull(),
  statusShort: varchar("status_short", { length: 20 }),
  minute: integer("minute"),
  homeScoreFullTime: integer("home_score_full_time"),
  awayScoreFullTime: integer("away_score_full_time"),
  homeScoreHalfTime: integer("home_score_half_time"),
  awayScoreHalfTime: integer("away_score_half_time"),
  homeScoreExtraTime: integer("home_score_extra_time"),
  awayScoreExtraTime: integer("away_score_extra_time"),
  homeScorePenalty: integer("home_score_penalty"),
  awayScorePenalty: integer("away_score_penalty"),
  winner: varchar("winner", { length: 20 }),
  referee: text("referee"),
  attendance: integer("attendance"),
  bet365HomeOdds: varchar("bet365_home_odds", { length: 10 }),
  bet365DrawOdds: varchar("bet365_draw_odds", { length: 10 }),
  bet365AwayOdds: varchar("bet365_away_odds", { length: 10 }),
  bet365OddsUpdatedAt: timestamp("bet365_odds_updated_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => ({
  statusIdx: index("fixtures_status_idx").on(table.status),
  matchDateIdx: index("fixtures_match_date_idx").on(table.matchDate),
  fixtureDateIdx: index("fixtures_fixture_date_idx").on(table.fixtureDate),
  leagueSeasonIdx: index("fixtures_league_season_idx").on(table.leagueId, table.seasonYear),
}));

export const matchEvents = pgTable("match_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  matchId: varchar("match_id").notNull(),
  eventType: text("event_type").notNull(),
  minute: integer("minute").notNull(),
  team: text("team").notNull(),
  player: text("player"),
  assistPlayer: text("assist_player"),
  details: jsonb("details"),
  occurredAt: timestamp("occurred_at").notNull().defaultNow(),
});

export const liveMatchSnapshots = pgTable("live_match_snapshots", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  matchId: varchar("match_id").notNull(),
  league: text("league").notNull(),
  status: text("status").notNull(),
  minute: integer("minute"),
  homeTeam: text("home_team").notNull(),
  awayTeam: text("away_team").notNull(),
  homeScore: integer("home_score"),
  awayScore: integer("away_score"),
  halfTimeHomeScore: integer("half_time_home_score"),
  halfTimeAwayScore: integer("half_time_away_score"),
  homeCorners: integer("home_corners"),
  awayCorners: integer("away_corners"),
  snapshotAt: timestamp("snapshot_at").notNull().defaultNow(),
});

export const odds = pgTable("odds", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  fixtureId: varchar("fixture_id").notNull().references(() => fixtures.id, { onDelete: "cascade" }),
  bookmaker: varchar("bookmaker", { length: 50 }).notNull(),
  market: varchar("market", { length: 50 }).notNull(),
  outcome: varchar("outcome", { length: 50 }).notNull(),
  oddsValue: varchar("odds_value", { length: 10 }).notNull(),
  oddsType: varchar("odds_type", { length: 20 }).notNull().default("pre_match"),
  threshold: varchar("threshold", { length: 10 }),
  lastUpdatedAt: timestamp("last_updated_at").notNull().defaultNow(),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  fixtureMarketIdx: index("odds_fixture_market_idx").on(table.fixtureId, table.market),
  activeIdx: index("odds_active_idx").on(table.isActive),
  uniqueOdds: uniqueIndex("odds_unique_idx").on(table.fixtureId, table.bookmaker, table.market, table.outcome),
}));

export const oddsHistory = pgTable("odds_history", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  oddsId: varchar("odds_id").notNull().references(() => odds.id, { onDelete: "cascade" }),
  oddsValue: varchar("odds_value", { length: 10 }).notNull(),
  snapshotAt: timestamp("snapshot_at").notNull().defaultNow(),
});

export const predictions = pgTable("predictions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  fixtureId: varchar("fixture_id").notNull().references(() => fixtures.id, { onDelete: "cascade" }),
  market: text("market"),
  predictedOutcomeText: text("predicted_outcome_text").notNull(),
  confidenceScore: integer("confidence_score").notNull(),
  confidenceLevel: varchar("confidence_level", { length: 20 }).notNull(),
  recommendedOdds: varchar("recommended_odds", { length: 10 }),
  marketOdds: varchar("market_odds", { length: 10 }),
  currentMarketOdds: varchar("current_market_odds", { length: 10 }),
  oddsLastUpdated: timestamp("odds_last_updated"),
  bookmakerName: text("bookmaker_name"),
  edge: integer("edge"),
  isValueBet: boolean("is_value_bet").default(false),
  reasoning: text("reasoning"),
  analysisData: jsonb("analysis_data"),
  oddsHistory: jsonb("odds_history"),
  userInteractions: jsonb("user_interactions"),
  isPremium: boolean("is_premium").notNull().default(false),
  predictionType: varchar("prediction_type", { length: 20 }).notNull(),
  generatedAtMinute: integer("generated_at_minute"),
  currentScore: jsonb("current_score"),
  expiresAt: timestamp("expires_at"),
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  settledAt: timestamp("settled_at"),
  settlementNotes: text("settlement_notes"),
  tipEndTime: timestamp("tip_end_time"),
  isProcessed: boolean("is_processed").notNull().default(false),
  processedAt: timestamp("processed_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => ({
  statusIdx: index("predictions_status_idx").on(table.status),
  premiumIdx: index("predictions_premium_idx").on(table.isPremium),
  dateIdx: index("predictions_date_idx").on(table.createdAt),
  fixtureIdx: index("predictions_fixture_idx").on(table.fixtureId),
}));

export const telegramAccounts = pgTable("telegram_accounts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  telegramId: varchar("telegram_id", { length: 50 }).notNull().unique(),
  telegramUsername: text("telegram_username"),
  telegramFirstName: text("telegram_first_name"),
  telegramLastName: text("telegram_last_name"),
  telegramPhotoUrl: text("telegram_photo_url"),
  linkMode: varchar("link_mode", { length: 20 }).notNull().default("linked"),
  notificationsEnabled: boolean("notifications_enabled").notNull().default(true),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => ({
  userIdIdx: index("telegram_accounts_user_id_idx").on(table.userId),
}));

export const telegramInviteLinks = pgTable("telegram_invite_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  inviteLink: text("invite_link").notNull().unique(),
  chatId: varchar("chat_id", { length: 50 }).notNull(),
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  telegramMemberId: varchar("telegram_member_id", { length: 50 }),
  joinedAt: timestamp("joined_at"),
  revokedAt: timestamp("revoked_at"),
  revokeReason: text("revoke_reason"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  userIdIdx: index("telegram_invite_links_user_id_idx").on(table.userId),
  statusIdx: index("telegram_invite_links_status_idx").on(table.status),
}));

export const telegramMessageLogs = pgTable("telegram_message_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  channelType: varchar("channel_type", { length: 10 }).notNull(),
  chatId: varchar("chat_id", { length: 50 }).notNull(),
  messageId: varchar("message_id", { length: 50 }),
  contentType: varchar("content_type", { length: 20 }).notNull(),
  tipId: varchar("tip_id", { length: 50 }).references(() => predictions.id, { onDelete: "cascade" }),
  celebrationId: varchar("celebration_id", { length: 50 }).references(() => predictions.id, { onDelete: "cascade" }),
  contentId: varchar("content_id", { length: 50 }),
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  errorMessage: text("error_message"),
  sentAt: timestamp("sent_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  contentIdIdx: index("telegram_message_logs_content_id_idx").on(table.contentId),
  tipIdIdx: index("telegram_message_logs_tip_id_idx").on(table.tipId),
  celebrationIdIdx: index("telegram_message_logs_celebration_id_idx").on(table.celebrationId),
  statusIdx: index("telegram_message_logs_status_idx").on(table.status),
  channelTypeIdx: index("telegram_message_logs_channel_type_idx").on(table.channelType),
}));

export const sessions = pgTable("session", {
  sid: varchar("sid").primaryKey().notNull(),
  sess: json("sess").notNull(),
  expire: timestamp("expire", { mode: 'string', precision: 6 }).notNull(),
}, (table) => ({
  expireIdx: index("session_expire_idx").on(table.expire),
}));

export const subscriptionPlans = pgTable("subscription_plans", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  slug: varchar("slug", { length: 50 }).notNull().unique(),
  tier: varchar("tier", { length: 20 }).notNull(),
  amountCents: integer("amount_cents").notNull(),
  currency: varchar("currency", { length: 3 }).notNull().default("USD"),
  interval: varchar("interval", { length: 20 }).notNull(),
  intervalCount: integer("interval_count").notNull().default(1),
  trialDays: integer("trial_days").notNull().default(0),
  isActive: boolean("is_active").notNull().default(true),
  metadata: jsonb("metadata"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const customers = pgTable("customers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }).unique(),
  gatewayCustomerId: varchar("gateway_customer_id"),
  gateway: varchar("gateway", { length: 20 }).notNull().default("nmi"),
  email: text("email").notNull(),
  name: text("name"),
  billingAddress: jsonb("billing_address"),
  metadata: jsonb("metadata"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const paymentMethods = pgTable("payment_methods", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  customerId: varchar("customer_id").notNull().references(() => customers.id, { onDelete: "cascade" }),
  gatewayPaymentMethodId: varchar("gateway_payment_method_id").notNull(),
  gateway: varchar("gateway", { length: 20 }).notNull().default("nmi"),
  type: varchar("type", { length: 20 }).notNull(),
  brand: varchar("brand", { length: 20 }),
  last4: varchar("last4", { length: 4 }),
  expMonth: integer("exp_month"),
  expYear: integer("exp_year"),
  isDefault: boolean("is_default").notNull().default(false),
  isActive: boolean("is_active").notNull().default(true),
  metadata: jsonb("metadata"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => ({
  customerIdx: index("payment_methods_customer_id_idx").on(table.customerId),
  gatewayIdx: index("payment_methods_gateway_idx").on(table.gatewayPaymentMethodId),
}));

export const invoices = pgTable("invoices", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  customerId: varchar("customer_id").notNull().references(() => customers.id, { onDelete: "cascade" }),
  subscriptionId: varchar("subscription_id"),
  status: varchar("status", { length: 20 }).notNull().default("draft"),
  amountCents: integer("amount_cents").notNull(),
  amountDueCents: integer("amount_due_cents").notNull(),
  amountPaidCents: integer("amount_paid_cents").notNull().default(0),
  currency: varchar("currency", { length: 3 }).notNull().default("USD"),
  description: text("description"),
  periodStart: timestamp("period_start"),
  periodEnd: timestamp("period_end"),
  dueDate: timestamp("due_date"),
  paidAt: timestamp("paid_at"),
  metadata: jsonb("metadata"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => ({
  customerStatusIdx: index("invoices_customer_status_idx").on(table.customerId, table.status),
  subscriptionIdx: index("invoices_subscription_idx").on(table.subscriptionId),
}));

export const transactions = pgTable("transactions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  customerId: varchar("customer_id").notNull().references(() => customers.id, { onDelete: "cascade" }),
  invoiceId: varchar("invoice_id").references(() => invoices.id, { onDelete: "set null" }),
  paymentMethodId: varchar("payment_method_id").references(() => paymentMethods.id, { onDelete: "set null" }),
  gatewayTransactionId: varchar("gateway_transaction_id"),
  gateway: varchar("gateway", { length: 20 }).notNull().default("nmi"),
  type: varchar("type", { length: 20 }).notNull(),
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  amountCents: integer("amount_cents").notNull(),
  currency: varchar("currency", { length: 3 }).notNull().default("USD"),
  description: text("description"),
  failureCode: varchar("failure_code", { length: 50 }),
  failureMessage: text("failure_message"),
  gatewayResponse: jsonb("gateway_response"),
  metadata: jsonb("metadata"),
  processedAt: timestamp("processed_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  customerIdx: index("transactions_customer_id_idx").on(table.customerId),
  invoiceIdx: index("transactions_invoice_id_idx").on(table.invoiceId),
  gatewayTxIdx: index("transactions_gateway_tx_idx").on(table.gatewayTransactionId),
  uniqueGatewayTxIdx: uniqueIndex("transactions_gateway_tx_unique_idx").on(table.gatewayTransactionId),
  statusIdx: index("transactions_status_idx").on(table.status),
}));

export const subscriptions = pgTable("subscriptions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  customerId: varchar("customer_id").notNull().references(() => customers.id, { onDelete: "cascade" }),
  planId: varchar("plan_id").notNull().references(() => subscriptionPlans.id),
  paymentMethodId: varchar("payment_method_id").references(() => paymentMethods.id),
  status: varchar("status", { length: 20 }).notNull().default("trialing"),
  currentPeriodStart: timestamp("current_period_start"),
  currentPeriodEnd: timestamp("current_period_end"),
  trialStart: timestamp("trial_start"),
  trialEnd: timestamp("trial_end"),
  canceledAt: timestamp("canceled_at"),
  cancelAtPeriodEnd: boolean("cancel_at_period_end").notNull().default(false),
  endedAt: timestamp("ended_at"),
  latestInvoiceId: varchar("latest_invoice_id"),
  gracePeriodEndsAt: timestamp("grace_period_ends_at"),
  dunningAttempts: integer("dunning_attempts").notNull().default(0),
  lastDunningAttemptAt: timestamp("last_dunning_attempt_at"),
  metadata: jsonb("metadata"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => ({
  customerIdx: index("subscriptions_customer_id_idx").on(table.customerId),
  statusIdx: index("subscriptions_status_idx").on(table.status),
  planIdx: index("subscriptions_plan_idx").on(table.planId),
}));

export const webhookEvents = pgTable("webhook_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  eventId: varchar("event_id").notNull().unique(),
  gateway: varchar("gateway", { length: 20 }).notNull().default("nmi"),
  eventType: varchar("event_type", { length: 50 }).notNull(),
  payload: jsonb("payload").notNull(),
  signature: text("signature"),
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  processingError: text("processing_error"),
  processedAt: timestamp("processed_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  eventIdIdx: uniqueIndex("webhook_events_event_id_idx").on(table.eventId),
  statusIdx: index("webhook_events_status_idx").on(table.status),
  typeIdx: index("webhook_events_type_idx").on(table.eventType),
}));

export const idempotencyKeys = pgTable("idempotency_keys", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  key: varchar("key").notNull().unique(),
  requestHash: varchar("request_hash").notNull(),
  resourceType: varchar("resource_type", { length: 50 }).notNull(),
  resourceId: varchar("resource_id"),
  responseStatus: integer("response_status").notNull(),
  responseBody: jsonb("response_body"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  keyIdx: uniqueIndex("idempotency_keys_key_idx").on(table.key),
  createdAtIdx: index("idempotency_keys_created_at_idx").on(table.createdAt),
}));

export const auditLogs = pgTable("audit_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  aggregateType: varchar("aggregate_type", { length: 50 }).notNull(),
  aggregateId: varchar("aggregate_id").notNull(),
  sequenceNumber: integer("sequence_number").notNull(),
  eventType: varchar("event_type", { length: 50 }).notNull(),
  payload: jsonb("payload").notNull(),
  checksum: varchar("checksum", { length: 64 }).notNull(),
  actorId: varchar("actor_id"),
  actorType: varchar("actor_type", { length: 20 }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  aggregateIdx: index("audit_logs_aggregate_idx").on(table.aggregateType, table.aggregateId),
  sequenceIdx: uniqueIndex("audit_logs_sequence_idx").on(table.aggregateType, table.aggregateId, table.sequenceNumber),
}));

export const reconciliationReports = pgTable("reconciliation_reports", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  reportDate: timestamp("report_date").notNull(),
  gateway: varchar("gateway", { length: 20 }).notNull().default("nmi"),
  totalInternalTransactions: integer("total_internal_transactions").notNull(),
  totalGatewayTransactions: integer("total_gateway_transactions").notNull(),
  matchedCount: integer("matched_count").notNull(),
  unmatchedInternalIds: jsonb("unmatched_internal_ids"),
  unmatchedGatewayIds: jsonb("unmatched_gateway_ids"),
  discrepancyCents: integer("discrepancy_cents").notNull().default(0),
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertUserSchema = createInsertSchema(users).omit({
  id: true,
  createdAt: true,
  subscriptionTier: true,
  stripeCustomerId: true,
  stripeSubscriptionId: true,
  subscriptionStatus: true,
});

export const insertPaymentRequestSchema = createInsertSchema(paymentRequests).omit({
  id: true,
  createdAt: true,
  processedAt: true,
  adminNotes: true,
  status: true,
});

export const insertUserEventSchema = createInsertSchema(userEvents).omit({
  id: true,
  createdAt: true,
});

export const insertLiveMatchSnapshotSchema = createInsertSchema(liveMatchSnapshots).omit({
  id: true,
  snapshotAt: true,
});

export const insertMatchEventSchema = createInsertSchema(matchEvents).omit({
  id: true,
  occurredAt: true,
});

export const paymentRequestSchema = z.object({
  email: z.string().email("Invalid email address"),
  paymentMethod: z.enum(['paypal', 'bank_transfer', 'revolut_wise', 'crypto_btc', 'crypto_usdt']),
});

export const registerSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z
    .string()
    .min(8, "Password must be at least 8 characters")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[a-z]/, "Password must contain at least one lowercase letter")
    .regex(/[0-9]/, "Password must contain at least one number"),
});

export const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(1, "Password is required"),
});

export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;
export type InsertPaymentRequest = z.infer<typeof insertPaymentRequestSchema>;
export type PaymentRequest = typeof paymentRequests.$inferSelect;
export type PaymentRequestInput = z.infer<typeof paymentRequestSchema>;
export type RegisterInput = z.infer<typeof registerSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type InsertUserEvent = z.infer<typeof insertUserEventSchema>;
export type UserEvent = typeof userEvents.$inferSelect;
export type InsertLiveMatchSnapshot = z.infer<typeof insertLiveMatchSnapshotSchema>;
export type LiveMatchSnapshot = typeof liveMatchSnapshots.$inferSelect;
export type InsertMatchEvent = z.infer<typeof insertMatchEventSchema>;
export type MatchEvent = typeof matchEvents.$inferSelect;

export const insertLeagueSchema = createInsertSchema(leagues).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertTeamSchema = createInsertSchema(teams).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertFixtureSchema = createInsertSchema(fixtures).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertOddsSchema = createInsertSchema(odds).omit({
  id: true,
  createdAt: true,
});

export const insertOddsHistorySchema = createInsertSchema(oddsHistory).omit({
  id: true,
});

export const insertPredictionSchema = createInsertSchema(predictions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type League = typeof leagues.$inferSelect;
export type InsertLeague = z.infer<typeof insertLeagueSchema>;

export type Team = typeof teams.$inferSelect;
export type InsertTeam = z.infer<typeof insertTeamSchema>;

export type Fixture = typeof fixtures.$inferSelect;
export type InsertFixture = z.infer<typeof insertFixtureSchema>;

export type Odds = typeof odds.$inferSelect;
export type InsertOdds = z.infer<typeof insertOddsSchema>;

export type OddsHistory = typeof oddsHistory.$inferSelect;
export type InsertOddsHistory = z.infer<typeof insertOddsHistorySchema>;

export type Prediction = typeof predictions.$inferSelect;
export type InsertPrediction = z.infer<typeof insertPredictionSchema>;

export const insertTelegramAccountSchema = createInsertSchema(telegramAccounts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertTelegramInviteLinkSchema = createInsertSchema(telegramInviteLinks).omit({
  id: true,
  createdAt: true,
});

export const insertTelegramMessageLogSchema = createInsertSchema(telegramMessageLogs).omit({
  id: true,
  createdAt: true,
});

export type TelegramAccount = typeof telegramAccounts.$inferSelect;
export type InsertTelegramAccount = z.infer<typeof insertTelegramAccountSchema>;

export type TelegramInviteLink = typeof telegramInviteLinks.$inferSelect;
export type InsertTelegramInviteLink = z.infer<typeof insertTelegramInviteLinkSchema>;

export type TelegramMessageLog = typeof telegramMessageLogs.$inferSelect;
export type InsertTelegramMessageLog = z.infer<typeof insertTelegramMessageLogSchema>;

export const insertPlanSchema = createInsertSchema(subscriptionPlans).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertCustomerSchema = createInsertSchema(customers).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertPaymentMethodSchema = createInsertSchema(paymentMethods).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertInvoiceSchema = createInsertSchema(invoices).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertTransactionSchema = createInsertSchema(transactions).omit({
  id: true,
  createdAt: true,
  processedAt: true,
});

export const insertSubscriptionSchema = createInsertSchema(subscriptions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertWebhookEventSchema = createInsertSchema(webhookEvents).omit({
  id: true,
  createdAt: true,
  processedAt: true,
});

export const insertIdempotencyKeySchema = createInsertSchema(idempotencyKeys).omit({
  id: true,
  createdAt: true,
});

export const insertAuditLogSchema = createInsertSchema(auditLogs).omit({
  id: true,
  createdAt: true,
});

export const insertReconciliationReportSchema = createInsertSchema(reconciliationReports).omit({
  id: true,
  createdAt: true,
});

export const insertSessionSchema = createInsertSchema(sessions);

export type Session = typeof sessions.$inferSelect;
export type InsertSession = z.infer<typeof insertSessionSchema>;

export type SubscriptionPlan = typeof subscriptionPlans.$inferSelect;
export type InsertSubscriptionPlan = z.infer<typeof insertPlanSchema>;

export type Customer = typeof customers.$inferSelect;
export type InsertCustomer = z.infer<typeof insertCustomerSchema>;

export type PaymentMethod = typeof paymentMethods.$inferSelect;
export type InsertPaymentMethod = z.infer<typeof insertPaymentMethodSchema>;

export type Invoice = typeof invoices.$inferSelect;
export type InsertInvoice = z.infer<typeof insertInvoiceSchema>;

export type Transaction = typeof transactions.$inferSelect;
export type InsertTransaction = z.infer<typeof insertTransactionSchema>;

export type Subscription = typeof subscriptions.$inferSelect;
export type InsertSubscription = z.infer<typeof insertSubscriptionSchema>;

export type WebhookEvent = typeof webhookEvents.$inferSelect;
export type InsertWebhookEvent = z.infer<typeof insertWebhookEventSchema>;

export type IdempotencyKey = typeof idempotencyKeys.$inferSelect;
export type InsertIdempotencyKey = z.infer<typeof insertIdempotencyKeySchema>;

export type AuditLog = typeof auditLogs.$inferSelect;
export type InsertAuditLog = z.infer<typeof insertAuditLogSchema>;

export type ReconciliationReport = typeof reconciliationReports.$inferSelect;
export type InsertReconciliationReport = z.infer<typeof insertReconciliationReportSchema>;
